/**
 * @fileoverview Calendar List View
 *
 * Renders every key date the host page hands to the calendar as a
 * sorted list, split into "Overdue & upcoming" and "Past". Reuses
 * the same coloured-pill style as the other calendar surfaces so the
 * visual vocabulary stays consistent. Each row leads with a
 * category icon + label (driven by the shared `CATEGORY_META`
 * registry) so the colour is always backed by an explicit category
 * indicator — the colours alone don't have to do all the
 * communication.
 *
 * Used by: `client-container.tsx` when `view === "list"`.
 */

import { useMemo } from "react";
import { format, parseISO, startOfDay } from "date-fns";
import { CalendarX2 } from "lucide-react";
import { cva } from "class-variance-authority";

import { useCalendar } from "@/calendar/contexts/use-calendar";

import { ScrollArea } from "@/ui/primitives/scroll-area";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";

import type { IEvent } from "@/calendar/interfaces";
import { CATEGORY_META } from "@/calendar/categoryMeta";
import { TaskMetaPills } from "@/calendar/components/task-meta-pills";
import { AssigneeControl } from "@/calendar/components/assignee-control";

interface IProps {
  events: IEvent[];
}

// ---------------------------------------------------------------------------
// Row palette — mirrors the existing month badge / agenda card CVAs so the
// list view doesn't introduce a third visual language for the same data.
// ---------------------------------------------------------------------------

const listRowVariants = cva(
  "group flex w-full select-none items-center gap-3 rounded-md border px-3 py-2 text-left text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
  {
    variants: {
      color: {
        blue: "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300 dark:hover:bg-blue-900",
        green:
          "border-green-200 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-800 dark:bg-green-950 dark:text-green-300 dark:hover:bg-green-900",
        red: "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-950 dark:text-red-300 dark:hover:bg-red-900",
        yellow:
          "border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300 dark:hover:bg-yellow-900",
        purple:
          "border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300 dark:hover:bg-purple-900",
        orange:
          "border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300 dark:hover:bg-orange-900",
        gray: "border-neutral-200 bg-neutral-50 text-neutral-900 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800",
        pink: "border-pink-200 bg-pink-50 text-pink-700 hover:bg-pink-100 dark:border-pink-800 dark:bg-pink-950 dark:text-pink-300 dark:hover:bg-pink-900",
      },
    },
    defaultVariants: { color: "blue" },
  },
);

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function CalendarListView({ events }: IProps) {
  const { onEventClick } = useCalendar();

  const { upcoming, past } = useMemo(() => splitByClock(events), [events]);

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20 text-muted-foreground">
        <CalendarX2 className="size-10" />
        <p className="text-sm md:text-base">
          No key dates match the current filters.
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[800px]" type="always">
      <div className="space-y-6 p-4">
        <ListSection
          title="Overdue & upcoming"
          empty="Nothing upcoming. Looks like a quiet stretch."
          events={upcoming}
          onEventClick={onEventClick}
        />
        <ListSection
          title="Past"
          empty="No past key dates yet."
          events={past}
          onEventClick={onEventClick}
        />
      </div>
    </ScrollArea>
  );
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function ListSection({
  title,
  empty,
  events,
  onEventClick,
}: {
  title: string;
  empty: string;
  events: IEvent[];
  onEventClick: (event: IEvent) => void;
}) {
  return (
    <section className="space-y-2">
      <div className="sticky top-0 z-10 -mx-4 flex items-center gap-2 border-b bg-background/95 px-4 py-2 backdrop-blur">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <Badge variant="outline" size="sm">
          {events.length}
        </Badge>
      </div>
      {events.length === 0 ? (
        <p className="px-2 py-3 text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-1.5">
          {events.map((event) => (
            <li key={event.id}>
              <ListRow event={event} onClick={() => onEventClick(event)} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ListRow({ event, onClick }: { event: IEvent; onClick: () => void }) {
  const start = parseISO(event.startDate);
  const date = format(start, "EEE d MMM yyyy");
  const time = !event.meta?.allDay ? format(start, "h:mm a") : null;
  // The date column follows the rendered placement: a planned ghost copy is
  // labelled "Planned" (MOR-696 toggle), everything else is its due date.
  const isPlannedMarker = event.meta?.marker === "planned";
  const dateLabel = isPlannedMarker ? "Planned" : "Due";

  const color = event.color;

  const categoryKey = event.meta?.category ?? "custom";
  const meta = CATEGORY_META[categoryKey];
  const CategoryIcon = meta.icon;

  // Every key date is assignable under MOR-696 (statutory rows materialise on
  // first touch), so the control shows whenever the row is targetable — i.e.
  // it already has a persisted id or a statutory code to materialise from.
  const taskMeta = event.meta;
  const canAssign = !!taskMeta && (!!taskMeta.persistedId || !!taskMeta.code);

  return (
    // Container holds the variant colour + hover; the main region is its own
    // button so the inline assignee popover isn't nested inside a button.
    <div
      className={cn(
        listRowVariants({ color }),
        "cursor-pointer",
        isPlannedMarker && "border-dashed opacity-80",
      )}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-none"
      >
        {/* Leading category icon — the colour + icon together
            communicate the category, so the colour isn't doing all the
            work on its own. */}
        <span
          className={cn(
            "inline-flex size-9 shrink-0 items-center justify-center rounded-lg border border-foreground/10 bg-background shadow-sm",
          )}
          title={meta.label}
        >
          <CategoryIcon className={cn("size-4", meta.iconFgClass)} />
        </span>

        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center gap-1.5">
            <p className="truncate font-semibold">{event.title}</p>
            {/* Inline category label so the practitioner sees the
                category in plain English without hunting for a legend. */}
            <span className="hidden shrink-0 text-[10px] font-medium opacity-70 sm:inline">
              · {meta.label}
            </span>
          </div>
          {event.description && (
            <p className="line-clamp-1 text-xs opacity-80">
              {event.description}
            </p>
          )}
          <TaskMetaPills meta={event.meta} />
        </div>
      </button>

      <div className="flex shrink-0 items-center gap-2 text-right">
        {/* Inline assignee — change ownership without opening the panel. */}
        {canAssign && taskMeta && (
          <AssigneeControl
            orgId={taskMeta.orgId}
            jobId={taskMeta.jobId}
            persistedId={taskMeta.persistedId}
            code={taskMeta.code}
            assigneeUserId={taskMeta.assigneeUserId}
          />
        )}
        <div className="flex flex-col items-end leading-tight">
          <span className="text-[9px] font-medium uppercase tracking-wide opacity-60">
            {dateLabel}
          </span>
          <span className="text-xs font-semibold tabular-nums">{date}</span>
          {time && <span className="text-[10px] opacity-70">{time}</span>}
        </div>
        {event.meta?.statusType === "completed" ? (
          <Badge color="green" size="sm" variant="soft">
            Done
          </Badge>
        ) : event.meta?.overdue && event.meta?.category !== "appointment" ? (
          <Badge color="red" size="sm" variant="soft">
            Overdue
          </Badge>
        ) : null}
        {event.meta?.jobName && (
          /* Solid contrasting pill so the job name stands out from
             the coloured row background instead of blending in. */
          <span
            title={event.meta.jobName}
            className="max-w-[180px] truncate rounded border border-foreground/20 bg-background px-1.5 py-0.5 text-[10px] font-semibold text-foreground shadow-sm"
          >
            {event.meta.jobName}
          </span>
        )}
      </div>
    </div>
  );
}

function splitByClock(events: IEvent[]): {
  upcoming: IEvent[];
  past: IEvent[];
} {
  const today = startOfDay(new Date()).getTime();
  const upcoming: IEvent[] = [];
  const past: IEvent[] = [];
  for (const event of events) {
    const start = parseISO(event.startDate).getTime();
    if (start >= today) {
      upcoming.push(event);
    } else {
      past.push(event);
    }
  }
  upcoming.sort(
    (a, b) => parseISO(a.startDate).getTime() - parseISO(b.startDate).getTime(),
  );
  past.sort(
    (a, b) => parseISO(b.startDate).getTime() - parseISO(a.startDate).getTime(),
  );
  return { upcoming, past };
}
